import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useQuery } from "@tanstack/react-query";
import getBuildingsInRegionList from "../http/getBuildingsInRegionList";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

const queryOptions = (opts?: Record<string, any>) => {
  return {
    cacheTime: 1000 * 60 * 60, // 1 hour
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
    refetchOnWindowFocus: false,
    retry: 3,
    ...opts,
  };
};

interface MapBounds {
  getNorth: () => number;
  getSouth: () => number;
  getWest: () => number;
  getEast: () => number;
  getNorthEast: () => { distanceTo: (sw: any) => number };
  getSouthWest: () => any;
  pad: (ratio: number) => MapBounds;
}

function getBounds(mapBounds: MapBounds | undefined, isSiteList: boolean) {
  if (mapBounds === undefined) return { north: 0, south: 0, west: 0, east: 0 };

  if (!isSiteList) {
    const boundsDistance = mapBounds
      .getNorthEast()
      .distanceTo(mapBounds.getSouthWest());
    // we want to pad the bounds so that we get all buildings that are near the edge of the map
    // to ensure that they don't appear as available
    // we choose 1500 because it is unlikely that a building is larger than 1500m
    const padRatio = 1500 / boundsDistance;
    mapBounds = mapBounds.pad(padRatio);
  }

  return {
    north: mapBounds.getNorth(),
    south: mapBounds.getSouth(),
    west: mapBounds.getWest(),
    east: mapBounds.getEast(),
  };
}

const useBuildingsInRegionList = (
  mapBounds: MapBounds | undefined,
  isSiteList: boolean,
  testOrgId?: string | null
) => {
  const { north, south, west, east } = getBounds(mapBounds, isSiteList);

  let mapSizeMeters = 100_000;
  if (mapBounds !== undefined)
    mapSizeMeters =
      mapBounds.getNorthEast().distanceTo(mapBounds.getSouthWest()) || 100_000;

  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  const buildingData = useQuery(
    ["building", north, south, west, east],
    async () => {
      return getBuildingsInRegionList(
        await getAuthTokenBlocking(getToken, relogin),
        north,
        south,
        west,
        east,
        testOrgId
      );
    },
    queryOptions({
      enabled: !!mapBounds && (mapSizeMeters < 7000 || isSiteList),
    })
  );

  return buildingData;
};

export default useBuildingsInRegionList;
